import { useCallback, useMemo, useRef } from "react";
import { Box, Paper, Typography, Stack } from "@mui/material";
import { useFormikContext, getIn } from "formik";
import Renderer from "./Renderer";
import { sortFields, humanizeSettingKey } from "../utils";
import { resolveSettingIcon } from "../settingIcon";
import { useTranslation } from "react-i18next";

export default function ObjectInput({
  id,
  schema,
  value,
  onChange,
  name,
  groupName,
  displayTitle = true,
  sxProps = {},
  locked,
}) {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  // Live refs so the (stable) child handler never reads stale form state.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const TitleIcon = resolveSettingIcon(schema?.icon);
  const properties = useMemo(() => schema?.properties || {}, [schema]);
  // The setting is the whole object (e.g. "callFunction3"); rebuild and POST it
  // under its name, not the leaf (which 404s). Array items pass through.
  const isArrayItem = typeof id === "string" && id.includes("[");

  // Fallback collapse for callFunction-style objects lacking backend showOn:
  // none → only action; callNumber → +phone; startScenario → +scenario.
  const hasActionProp = !!properties.action;
  const actionValue = hasActionProp ? getIn(values, `${id}.action`) : undefined;
  const isConventionVisible = (key, propSchema) => {
    if (propSchema?.showOn || propSchema?.hideOn) return true;
    if (!hasActionProp || key === "action") return true;
    if (actionValue == null || actionValue === "" || actionValue === "none") {
      return false;
    }
    if (key === "phoneNumber") return actionValue === "callNumber";
    if (key === "scenario") return actionValue === "startScenario";
    return true;
  };

  // Stable so FastField keeps a live handler (else it caches a stale onChange).
  const handleChildChange = useCallback(
    (childId, childValue) => {
      const emit = onChangeRef.current;
      if (isArrayItem || typeof childId !== "string") {
        emit?.(childId, childValue);
        return;
      }
      const base = getIn(valuesRef.current, id);
      const next = base && typeof base === "object" ? { ...base } : {};
      const relative = childId.startsWith(`${id}.`)
        ? childId.slice(id.length + 1)
        : childId.split(".").pop();
      const parts = relative.split(".");
      let cursor = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        cursor[parts[i]] = { ...(cursor[parts[i]] || {}) };
        cursor = cursor[parts[i]];
      }
      cursor[parts[parts.length - 1]] = childValue;
      emit?.({ [name]: next });
    },
    [isArrayItem, id, name]
  );
  const sortedProperties = useMemo(
    () =>
      Object.entries(properties).sort(([aKey, aValue], [bKey, bValue]) =>
        sortFields(aValue, bValue),
      ),
    [properties],
  );
  const isHidden = useMemo(() => {
    return Object.entries(schema.properties).every(
      ([key, prop]) => prop.hideOn && prop.hideOn[""] === "",
    );
  }, [schema]);

  if (isHidden) {
    return null;
  }

  return (
    <Paper
      sx={{
        p: 1,
        bgcolor: "grey.50",
        border: 1,
        borderColor: "grey.300",
        ...sxProps,
      }}
    >
      {displayTitle ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {TitleIcon && <TitleIcon color="action" size={0.85} />}
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {t(name, { defaultValue: humanizeSettingKey(name) })}
          </Typography>
        </Box>
      ) : null}
      <Stack spacing={2}>
        {sortedProperties
          .filter(([key, propertySchema]) =>
            isConventionVisible(key, propertySchema)
          )
          .map(([key, propertySchema]) => (
            <Renderer
              key={key}
              id={`${id}.${key}`}
              name={key}
              value={value?.[key]}
              schema={propertySchema}
              groupName={groupName}
              locked={locked}
              onChange={handleChildChange}
              // callFunction phone gets the address-book picker.
              addressBook={hasActionProp && key === "phoneNumber"}
            />
          ))}
      </Stack>
    </Paper>
  );
}
